import React, { useState } from 'react';
import AlertDialog from 'components/AlertDialog';
import { Disclosure } from 'further-ui/hooks';
import { SwapHoriz } from '@mui/icons-material';
import InvestorSearch from 'components/InvestorSearch';
import api, { type ApiResponses } from 'lib/trpcClient';
import { useNotification } from 'hooks/ui/useNotification';
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import Spacing from 'components/Spacing';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';

const StyledFlexContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  overflowX: 'hidden',
  flexWrap: 'wrap',
});

const StyledInvestorSearchWithMinWidth = styled(InvestorSearch)({
  minWidth: '360px',
});

type Props = {
  investmentId: string;
  reassignDisclosure: Disclosure;
  firmId?: string;
  currentInvestorId?: string;
};

const InvestmentReassign: React.FC<Props> = ({
  investmentId,
  reassignDisclosure,
  firmId,
  currentInvestorId,
}) => {
  const [selectedInvestorId, setSelectedInvestorId] = useState<string>();
  const notification = useNotification();
  const queryClient = useQueryClient();

  const reassignMutation = api.investment.reassignInvestor.useMutation(
    useHandleTrpcMutation<ApiResponses['investment']['reassignInvestor']>()({
      successMessage: 'Investment reassigned successfully.',
      onSuccess: (response) => {
        if (!response.success) {
          notification.error(
            'Investment reassignment failed. Please check your recipient and try again.',
          );
        } else {
          queryClient.invalidateQueries({
            queryKey: ['investments'],
          });
        }

        reassignDisclosure.onClose();
      },
    }),
  );

  const handleReassign = () => {
    if (!selectedInvestorId) return;
    reassignMutation.mutate({
      investmentId,
      newInvestorId: selectedInvestorId,
    });
  };

  return (
    <AlertDialog
      icon={SwapHoriz}
      open={reassignDisclosure.isOpen}
      onClose={reassignDisclosure.onClose}
      onConfirm={handleReassign}
      title="Reassign subscription"
      size="sm"
      content={
        <Spacing>
          <Typography>
            Select the investor to reassign this subscription to below.
          </Typography>
          <Typography>
            <b>Please note:</b> After transfer, deleting and remaking tax
            certificates will result in new certificates being in the
            transferee's name.
          </Typography>
          <Typography>
            <i>
              For deceased investors, use the freeze and transfer feature, not
              subscription reassignment.
            </i>
          </Typography>

          <StyledFlexContainer>
            <label htmlFor="selectNewInvestor">Select new investor:</label>

            <StyledInvestorSearchWithMinWidth
              selectedInvestorId={selectedInvestorId}
              onInvestorSelect={setSelectedInvestorId}
              firmId={firmId}
              excludeInvestorOptionById={[currentInvestorId ?? '']}
            />
          </StyledFlexContainer>
        </Spacing>
      }
      confirmBtnProps={{
        disabled: !selectedInvestorId || reassignMutation.isPending,
        loading: reassignMutation.isPending,
      }}
      btnLabels={{
        cancel: 'Go back',
        confirm: 'Reassign',
      }}
    />
  );
};

export default InvestmentReassign;
